import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getRoleList } from "reduxStore/action/roleActions";
import strings from "translate/language";
import TableDataNotFound from "component/TableDataNotFound";
import { errorToast, successToast } from "utils/httpClient";
import { handleListResponse, seo } from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import {
  channelPartnerSlug,
  channelPartnerUserType,
  cpAgentSlug,
} from "utils/constants";
import "./RoleListing.css";

const roleInitials = (title = "") => {
  const parts = String(title).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "R";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const Roles = () => {
  const navigate = useNavigate();
  const [roleList, setRoleList] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const dispatch = useDispatch();
  const getRoleListSelector = useSelector((state) => state.roleData) || {};

  const { create, edit } = usePermission({
    create: "add_role",
    edit: "edit_role",
  });
  useLayoutEffect(() => {
    dispatch(getRoleList());
  }, []);

  useEffect(() => {
    if (getRoleListSelector?.response) {
      if (getRoleListSelector?.response?.status === 200) {
        if (getRoleListSelector?.create) {
          successToast(getRoleListSelector?.response?.message);
        }
        setRoleList(
          handleListResponse(getRoleListSelector?.response?.data)
            ? getRoleListSelector?.response?.data
            : []
        );
      } else {
        errorToast(getRoleListSelector?.response?.message);
      }
    }
  }, [getRoleListSelector]);
  useEffect(() => {
    seo({
      title: "Role Listing",
      metaDescription: "Role listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const filteredRoles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return roleList;
    return roleList.filter((item) =>
      String(item.role_title || "")
        .toLowerCase()
        .includes(q)
    );
  }, [roleList, searchQuery]);

  const openRoleUsers = (item) => {
    const _id = item._id;
    if (item.slug === channelPartnerSlug || item.slug === cpAgentSlug) {
      const userType =
        item.slug === channelPartnerSlug
          ? channelPartnerUserType.cp
          : channelPartnerUserType.agent;
      navigatpage(`/channelpartner?role_id=${_id}&user_type=${userType}`);
      return;
    }
    navigatpage(`/user?role_id=${_id}`);
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchQuery("");
  };

  return (
    <div className="page-body justo-role-page">
      <Breadcrumb title={strings.role} titleactive={strings.roleListing} />
      <div className="container-fluid justo-role">
        <div className="justo-role__card">
          <div className="justo-role__top">
            <div>
              <h2 className="justo-role__title">{strings.listOfRole}</h2>
              <p className="justo-role__subtitle">
                {roleList.length} roles · review users and edit permissions.
              </p>
            </div>
            {create ? (
              <div className="justo-role__top-actions">
                <button
                  type="button"
                  className="justo-role__btn justo-role__btn--solid"
                  onClick={() => navigatpage("/addnewroles")}
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  {strings.addNewrole}
                </button>
              </div>
            ) : null}
          </div>

          <div className="justo-role__filters">
            <div className="justo-role__filters-grid">
              <div className="justo-role__field">
                <label className="justo-role__label" htmlFor="role_search">
                  {strings.search}
                </label>
                <input
                  id="role_search"
                  className="justo-role__input"
                  type="text"
                  autoComplete="off"
                  placeholder="Search by role name"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                />
              </div>
              <div className="justo-role__filter-actions">
                <button
                  type="button"
                  className="justo-role__btn justo-role__btn--solid"
                  onClick={handleSearch}
                >
                  <i className="fa fa-search" aria-hidden="true" />
                  {strings.search}
                </button>
                <button
                  type="button"
                  className="justo-role__btn justo-role__btn--ghost"
                  onClick={handleReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </div>

          <div className="justo-role__scroll">
            <table className="justo-role__table">
              <thead>
                <tr>
                  <th className="justo-role__col-sno" scope="col">
                    {strings.sno}
                  </th>
                  <th scope="col">{strings.roles}</th>
                  <th className="justo-role__col-users" scope="col">
                    {strings.total_users}
                  </th>
                  {edit ? (
                    <th className="justo-role__col-action" scope="col">
                      {strings.action}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {filteredRoles.length > 0
                  ? filteredRoles.map((item, index) => (
                      <tr key={item._id || index}>
                        <td className="justo-role__sno justo-role__col-sno">
                          {index + 1}
                        </td>
                        <td>
                          <div className="justo-role__role-cell">
                            <span
                              className="justo-role__avatar"
                              aria-hidden="true"
                            >
                              {roleInitials(item.role_title)}
                            </span>
                            <span className="justo-role__cell-primary">
                              {item.role_title}
                            </span>
                          </div>
                        </td>
                        <td className="justo-role__col-users">
                          <div className="justo-role__users">
                            <button
                              type="button"
                              className="justo-role__users-btn"
                              onClick={() => openRoleUsers(item)}
                            >
                              {item.total_user}
                            </button>
                          </div>
                        </td>
                        {edit ? (
                          <td className="justo-role__col-action">
                            <div className="justo-role__actions">
                              <button
                                type="button"
                                className="justo-role__btn justo-role__btn--outline justo-role__btn--sm"
                                title={strings.edit}
                                aria-label={strings.edit}
                                onClick={() =>
                                  navigatpage(`/editroles/${item._id}`)
                                }
                              >
                                <i className="fa fa-pencil" aria-hidden="true" />
                                {strings.edit}
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>
            <TableDataNotFound data={filteredRoles} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roles;
